import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";

import { Separator } from "src/components/separator/separator";
import { Body, PageTitle, SectionTitle, SubTitle } from "src/components/typography/typography";
import flourishingFaithModel from "../../assets/images/ffi_model.png";
import flourishingSchoolCultureModel from "../../assets/images/fsci_report_logo.png";

import "./about.scss";

/*
 * Main
 */

export const AboutPage = (): React.ReactElement => {
    const contactUsRef = React.createRef<HTMLDivElement>();
    const location = useLocation();

    useEffect(() => {
        function scrollToHashOnLoad(hash: string): void {
            if (location.hash === hash) {
                contactUsRef?.current?.scrollIntoView({
                    behavior: "smooth",
                    block: "start",
                });
            }
        }

        scrollToHashOnLoad("#contact-us");
    }, [location, contactUsRef]);

    return (
        <div className="main-page-wrap">
            <div className="center">
                <div className="page-section">
                    <PageTitle>About Flourishing School Culture</PageTitle>
                    <Body>
                        Founded in 1978, ACSI is an evangelical Christian school association with members around the
                        world. The Flourishing School Culture Model and the Flourishing Faith Model, along with their
                        accompanying instruments (the FSCI and FFI, respectively), were developed as exploratory tools
                        to identify and validate a roadmap to flourishing for Christian schools.
                    </Body>
                </div>
                <div className="models-wrap">
                    <div className="page-section models">
                        <div className="model">
                            <div className="model-image">
                                <img alt="Flourishing School Culture Model" src={flourishingSchoolCultureModel} />
                            </div>
                            <div className="model-text">
                                <SectionTitle>The Models</SectionTitle>
                                <Body>
                                    The Flourishing School Culture Model maps flourishing across five domains–Purpose,
                                    Relationships, Teaching & Learning, Expertise & Resources, and Well-Being–with 35
                                    validated constructs. Each domain provides clarity and context for interpreting and
                                    understanding flourishing in Christian schools.
                                </Body>
                            </div>
                        </div>
                        <div className="model">
                            <div className="model-image">
                                <img alt="Flourishing Faith Model" src={flourishingFaithModel} />
                            </div>
                            <Body className="model-text">
                                The Flourishing Faith Model provides deeper insights into the Purpose domain of the
                                Flourishing School Culture Model, with a focus on students’ faith formation and biblical
                                worldview development in the domains of Head, Heart, and Hands. Concurrently, the model
                                maps how the school supports student growth in these areas through intellectual,
                                nurturing, and practical efforts.
                            </Body>
                        </div>
                    </div>
                    <Body>
                        You can learn more about the model on{" "}
                        <a
                            href="https://www.acsi.org/flourishing-school-culture/model"
                            rel="noopener noreferrer"
                            target="_blank"
                        >
                            our website
                        </a>
                        .
                    </Body>
                </div>

                <Separator />
                <div className="page-section">
                    <div className="split-content-part part-left">
                        <SectionTitle>Using the Instrument to Flourish</SectionTitle>
                        <Body>
                            Now you can use the FSCI and FFI to assess your schools’ relative strengths and weaknesses
                            and track your progress in each construct year-to-year. Each year you sign up to use each
                            instrument, you will gain access to one new report that includes comparative data.
                        </Body>
                    </div>
                    <div className="split-content-part part-right">
                        <SectionTitle>About this Application</SectionTitle>
                        <Body>
                            Designed to streamline rolling administrations of both the FSCI and the FFI, this web-based
                            application is your hub for assessing and improving your school’s culture and flourishing
                            faith outcomes.
                        </Body>
                    </div>
                    <div className="clear" />
                </div>
                <Separator />
                <div className="page-section">
                    <SectionTitle>Frequently Asked Questions</SectionTitle>
                    <SubTitle>Q: How often do I administer the survey?</SubTitle>
                    <Body>
                        A: It is entirely up to schools to determine how frequently they wish to administer, based on
                        their calendar (which may include considerations like accreditation preparation/visit schedule,
                        use of other surveys, budget, etc.). There is no minimum or maximum frequency, though we would
                        not recommend administering a specific instrument (FSCI or FFI) more than once per academic
                        year. Please note that the FSCI can be administered jointly or separately.
                    </Body>
                    <Body>
                        For specific guidance on how the FSCI and FFI might fit in with your accreditation cycle, you
                        may wish to reach out to your accreditation representative for your region/country.
                    </Body>
                    <SubTitle>Q: Would the surveys be beneficial to a small school as we begin our process?</SubTitle>
                    <Body>
                        A: Yes, the instruments would be entirely appropriate for a) a school of any size, as we had a
                        good representation of small, medium, and large schools in both the FSCI and FFI samples, and b)
                        for a school pursuing accreditation, as the instruments are approved to satisfy the survey
                        requirements of ACSI (and therefore partner agency) accreditation.
                    </Body>
                    <SubTitle>Q: Do the questions assume survey takers are Christians?</SubTitle>
                    <Body>
                        A: The surveys do not assume that students are Christians, though they do, for example, ask them
                        about their relationship with God in terms of faith development, questioning/doubts, and views
                        of Christians. We also ask comparable questions of alumni. In that sense, it might be helpful in
                        gauging the school’s impact in spiritual formation for students (Christian and non-Christian).
                    </Body>
                    <SubTitle>Q: May we contact schools that have already taken the survey?</SubTitle>
                    <Body>
                        A: Unfortunately, our research protocol does not allow us to share names of schools that have
                        participated. We can, however, convey where schools are (as well as enrollment/size,
                        accreditation rate, etc.) so other schools can judge if the sample might be reflective of their
                        school.
                    </Body>
                    <SubTitle>Q: Can we remove or reword questions?</SubTitle>
                    <Body>
                        A: No, our research protocol does not allow for items to be eliminated or reworded. We ask that
                        you maintain the integrity of the research by not modifying the surveys.
                    </Body>
                    <SubTitle>Q: What if my alumni pool is small?</SubTitle>
                    <Body>
                        A: We still encourage you to administer the surveys! Please keep in mind that alumni do not have
                        to be high school graduates. They can be anyone older than the minimum grade for inclusion (6th
                        grade for FSCI, 8th for FFI) who previously attended the school. You can also invite "graduates"
                        of a pre-existing elementary program if that is relevant.
                    </Body>
                    <SubTitle>
                        Q: I would like to register our school for this research and am unable to pay by credit card.
                    </SubTitle>
                    <Body>
                        A: We are happy to assist you with that process. Please email{" "}
                        <a href="mailto:assessmentsupport@acsi.org">assessmentsupport@acsi.org</a> for additional
                        support.
                    </Body>
                    <SubTitle>
                        Q: Can you give me more details about the surveys, such as how long the assessments take to
                        administer and what format they are given in? Can you give me a link to that information?
                    </SubTitle>
                    <Body>
                        A: Surveys for all seven constituents are electronic. Schools will email the links (we provide
                        the links and sample invitation language) to those constituents. The exception is students in
                        grades 6-12, who will typically take the assessment in a computer lab or during a set class on
                        devices. The surveys should take about 5-15 minutes to complete for each constituent. There is a
                        longer “metrics” survey that provides basic demographic information about the school that the
                        administrator or contact person completes, which can take a bit longer. For a sneak peak at the
                        FSCI side of the administration platform and what the process looks like, check out this
                        3-minute{" "}
                        <a href="https://vimeo.com/572150543" rel="noopener noreferrer" target="_blank">
                            video
                        </a>
                        .
                    </Body>
                    <SubTitle>Q: Can I see the questions before I administer the surveys?</SubTitle>
                    <Body>
                        A: As is standard for 360-culture-level assessments, we do not provide the questions for review.
                        This is largely because they are validated questions that are linked to constructs. Thus, the
                        constructs should give you some sense of what the questions are (you can download the national
                        report, which provides a definition of each construct, for the FSCI{" "}
                        <a
                            href="https://www.acsi.org/docs/default-source/website-publishing/research/fsci-leadership-report-2021.pdf?sfvrsn=eceffe30_0"
                            rel="noopener noreferrer"
                            target="_blank"
                        >
                            here
                        </a>{" "}
                        and for the FFI{" "}
                        <a
                            href="https://www.acsi.org/docs/default-source/website-publishing/research/flourishing-faith.pdf"
                            rel="noopener noreferrer"
                            target="_blank"
                        >
                            here
                        </a>
                        ).
                    </Body>
                    <SubTitle>Q: Are the tools also applicable to international schools?</SubTitle>
                    <Body>
                        A: Yes! The FSCI and the FFI were developed and tested with both US schools and schools in other
                        countries.
                    </Body>
                    <SubTitle>Q: Can I get a copy of the surveys and translate them?</SubTitle>
                    <Body>
                        A: While we hope to be able to do this/make this possible in the future, right now the
                        instruments are only tested and valid with English-speaking populations.
                    </Body>
                </div>
                <Separator />
                <div className="page-section" ref={contactUsRef}>
                    <SectionTitle>Contact Us</SectionTitle>
                    <Body>
                        Questions about the FSCI or FFI? Email{" "}
                        <a href="mailto:assessmentsupport@acsi.org">assessmentsupport@acsi.org</a>.
                    </Body>
                </div>
            </div>
        </div>
    );
};
